import React, { useState } from 'react';
import styled from 'styled-components';
import TradeSetBar from '../tradeset/TradeSetBar';
import { Preset, TradeSet } from '../../types/preset';
import { toast } from 'react-toastify';

interface ChartContainerProps {
  selectedSymbol: string;
}

const Container = styled.div`
  position: relative; /* Create a stacking context */
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.background.primary};
  border-radius: ${({ theme }) => theme.shape.borderRadius.large};
  overflow: hidden; /* Ensure children don't overflow the rounded corners */
`;

const PlaceholderChart = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: #1a202c; /* Darker background */
  color: #e5e7eb; /* Light text */
  font-size: 1.5rem;
  font-weight: 500;
  position: relative;
  padding-bottom: 100px; /* Increased padding for the TradeSetBar */
`;

const ChartSymbol = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.spacing(2)};
  left: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(0.5)} ${({ theme }) => theme.spacing(1.5)};
  background-color: ${({ theme }) => theme.colors.background.paper};
  border-radius: ${({ theme }) => theme.shape.borderRadius.medium};
  font-size: 0.9rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

// Simple SVG line chart mockup
const ChartMockup = () => (
  <svg width="80%" height="60%" viewBox="0 0 500 200" style={{ stroke: '#f59e0b', fill: 'none', strokeWidth: 2 }}>
    <path d="M0,150 C50,120 80,170 100,90 C120,50 150,110 200,70 C250,30 300,80 340,60 C380,40 400,80 430,50 C460,20 480,50 500,40" />
    {/* Add a grid pattern */}
    <g stroke="#2d3748" stroke-width="0.5">
      {/* Horizontal grid lines */}
      {Array.from({length: 10}).map((_, i) => (
        <line key={`h-${i}`} x1="0" y1={i * 20} x2="500" y2={i * 20} />
      ))}
      {/* Vertical grid lines */}
      {Array.from({length: 20}).map((_, i) => (
        <line key={`v-${i}`} x1={i * 25} y1="0" x2={i * 25} y2="200" />
      ))}
    </g>
    {/* Price points */}
    <circle cx="100" cy="90" r="3" fill="#f59e0b" />
    <circle cx="200" cy="70" r="3" fill="#f59e0b" />
    <circle cx="340" cy="60" r="3" fill="#f59e0b" />
    <circle cx="430" cy="50" r="3" fill="#f59e0b" />
  </svg>
);

const ChartContainer: React.FC<ChartContainerProps> = ({ selectedSymbol }) => {
  // Add console log to verify rendering
  console.log("ChartContainer rendering with symbol:", selectedSymbol);
  
  // In a real implementation, you might want to handle these in a parent component
  // and pass them down as props
  const handleExecutePreset = async (preset: Preset) => {
    toast.info(`Executing preset ${preset.name} from Chart`);
    // Actual implementation would go here
  };

  const handleExecuteTradeSet = async (tradeSet: TradeSet) => {
    toast.info(`Executing tradeSet ${tradeSet.name} from Chart`);
    // Actual implementation would go here
  };

  return (
    <Container>
      <PlaceholderChart>
        <ChartSymbol>{selectedSymbol}</ChartSymbol>
        <div>Charting UI (placeholder for now)</div>
        <ChartMockup />
      </PlaceholderChart>
      
      {/* Add the TradeSetBar */}
      <TradeSetBar 
        selectedSymbol={selectedSymbol}
        onExecutePreset={handleExecutePreset}
        onExecuteTradeSet={handleExecuteTradeSet}
      />
      {/* Debug element to always be visible */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: 'rgba(59, 130, 246, 0.2)',
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '10px',
        color: '#e5e7eb',
        zIndex: 999
      }}>
        TradeSetBar should appear above this line
      </div>
    </Container>
  );
};

export default ChartContainer; 